import { View, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Caption, Body } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import type { NavigationPrompt } from '@/services/navigation';

type MapNextUpBarProps = {
  prompt: NavigationPrompt;
  onGetDirections: () => void;
  onFocusActivity?: () => void;
  onDismiss?: () => void;
};

const formatMinutes = (minutes: number): string => {
  const m = Math.max(0, Math.round(minutes));
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rem = m % 60;
    return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
  }
  return `${m} min`;
};

export function MapNextUpBar({
  prompt,
  onGetDirections,
  onFocusActivity,
  onDismiss,
}: MapNextUpBarProps) {
  const theme = useTheme();
  const travelMinutes =
    prompt.travelDurationMinutes ??
    Math.max(1, Math.round(prompt.fastestRoute.duration / 60));
  const leaveMinutes = prompt.leaveInMinutes >= 0 ? prompt.leaveInMinutes : 0;
  const leaveLabel = leaveMinutes > 0 ? `Leave in ${formatMinutes(leaveMinutes)}` : 'Leave now';
  const modeLabel =
    prompt.fastestRoute.mode === 'walking'
      ? 'walk'
      : prompt.fastestRoute.mode === 'transit'
        ? 'transit'
        : 'drive';

  return (
    <View
      style={{
        marginHorizontal: theme.spacing(1.5),
        marginBottom: theme.spacing(0.75),
        borderRadius: theme.radii.lg,
        borderWidth: 1,
        borderColor: theme.colors.primary + '44',
        backgroundColor: theme.colors.surface,
        padding: theme.spacing(1.25),
        gap: theme.spacing(1),
        shadowColor: '#000',
        shadowOpacity: 0.08,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 2 },
        elevation: 3,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(0.75) }}>
        <Ionicons name="navigate-circle" size={22} color={theme.colors.primary} style={{ marginTop: 2 }} />
        <View style={{ flex: 1, minWidth: 0, gap: theme.spacing(0.25) }}>
          <Caption color={theme.colors.textSecondary} style={{ fontWeight: '700', textTransform: 'uppercase', fontSize: 11 }}>
            Next up
          </Caption>
          <Pressable onPress={onFocusActivity} disabled={!onFocusActivity} accessibilityRole="button">
            <Body style={{ fontWeight: '700' }} numberOfLines={2}>
              {prompt.nextActivity.title}
            </Body>
          </Pressable>
          <Caption color={theme.colors.textSecondary}>
            {leaveLabel} · ~{formatMinutes(travelMinutes)} {modeLabel}
            {prompt.nextActivity.startTime ? ` · ${prompt.nextActivity.startTime}` : ''}
          </Caption>
        </View>
        {onDismiss ? (
          <Pressable
            onPress={onDismiss}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel="Dismiss next up"
          >
            <Ionicons name="close" size={18} color={theme.colors.textSecondary} />
          </Pressable>
        ) : null}
      </View>
      <Button
        label="Get directions"
        size="small"
        onPress={onGetDirections}
        icon={<Ionicons name="navigate" size={16} color={theme.colorScheme === 'dark' ? '#000' : '#FFF'} />}
      />
    </View>
  );
}
